import { Injectable, Injector } from '@angular/core';
import { CookieServices } from '@app/core/services/cookie-service/cookie-service.component';
import { GlobalThemeService } from '@app/services/global-theme-service/global.theme.service';
import { Observable } from 'rxjs';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '@app/core/config.service';
import moment from 'moment';

import { defaultLanguage } from '@app/core/constants/common-constants';
import {AuthService} from "@core/services/auth/auth.service";
import { hasSessionSignal } from '@core/services/auth/auth-init.helper';
import { authDebug } from '@core/services/auth/auth-debug.util';
import { filter, take, switchMap } from 'rxjs/operators';

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
    cookieService: CookieServices;

    constructor(
        private injector: Injector,
        private configService: ConfigService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.url.includes('/config')) {
            return next.handle(req);
        }

        const globalThemeService = this.injector.get(GlobalThemeService);
        const authService = this.injector.get(AuthService);
        this.cookieService = this.injector.get(CookieServices);
        const config: any = this.configService.config;
        const sessionReady = hasSessionSignal(authService);
        const swidCookie = this.cookieService.getSwidCookie();

        authDebug('AuthHttpInterceptor intercept', {
            url: req.url,
            isInitialized: authService.isInitialized.value,
            sessionReady,
            hasAccessToken: !!authService.accessToken,
            hasSwidInMemory: !!authService.swid,
            hasSwidCookie: !!swidCookie,
            willWaitForAuth: !authService.isInitialized.value && !sessionReady
        });

        // Match auth-init.helper: a site SWID cookie is not an OLCI bearer session.
        if (!authService.isInitialized.value && !sessionReady) {
            authDebug('AuthHttpInterceptor: deferring request until isInitialized', { url: req.url });
            return authService.isInitialized.pipe(
                filter(isInit => isInit),
                take(1),
                switchMap(() => {
                    authDebug('AuthHttpInterceptor: isInitialized true — releasing deferred request', { url: req.url });
                    return this.addHeadersAndHandle(req, next, authService, globalThemeService, config);
                })
            );
        }

        return this.addHeadersAndHandle(req, next, authService, globalThemeService, config);
    }

    private addHeadersAndHandle(
        req: HttpRequest<any>,
        next: HttpHandler,
        authService: AuthService,
        globalThemeService: GlobalThemeService,
        config: any
    ): Observable<HttpEvent<any>> {
        let headers = new HttpHeaders();

        // SWID header (fall back to cookie if not set yet)
        const swid = authService.swid || this.cookieService.getSwidCookie();
        if (swid) {
            headers = headers.append('swid', swid);
        }

        if (authService.accessToken) {
            headers = headers.append('authorization', 'BEARER ' + authService.accessToken);
        }
        authDebug('AuthHttpInterceptor: sending request', {
            url: req.url,
            hasAuthorizationHeader: headers.has('authorization'),
            hasSwidHeader: headers.has('swid'),
            campus: globalThemeService.getCampus()
        });
        headers = headers.append('x-app-id', config.xAppIdHeader[globalThemeService.getCampus()]);
        headers = headers.append('x-disney-ui', 'web');
        headers = headers.append('x-request-timestamp', moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'));
        const lang = req.headers['x-disney-content-lang'] ||
            req.headers['Accept-Language'] ||
            this.getCookieLanguageString() ||
            defaultLanguage;
        headers = headers.append('x-disney-content-lang', lang);

        // Get the app client Id header add it to the intercepted request
        // Use the req.headers value if it exists (APP Flows),
        //  otherwise recalculate the value (value won't matter in Non-APP flows)
        const inferredPaymentUiHeader = req.headers.get('x-disney-app-client-id') ||
            globalThemeService.getAPPClientHeader();
        headers = headers.append('x-disney-app-client-id', inferredPaymentUiHeader);

        delete headers['origin'];

        const clonedReq = req.clone({
            headers: headers,
            withCredentials: true
        });
        return next.handle(clonedReq);
    }

    // This currently isn't used. This was a stop gap solution for getting Language header for Aulani.
    // The My Aulani Vacation site doesn't support multiple languages, only english, so this isn't needed
    getCookieLanguageString() {
        const langCookie = this.cookieService.getLocale();
        if (!langCookie) {
            return void 0;
        }
        const decodedCookie = decodeURIComponent(langCookie);
        try {
            const langObj = JSON.parse(decodedCookie);
            const lang = langObj.preferredLanguage;
            return lang;
        } catch (err) {
            return void 0;
        }
    }
}
